/**
 * DiagnosticChatManager - Manages diagnostic test mode in the chat interface
 * 
 * This module handles:
 * - Detecting pending diagnostic tests from Tutor Hub
 * - Starting diagnostic chat sessions via API
 * - Routing user answers through the diagnostic adapter
 * - Tracking progress and completion
 * - Returning to Tutor Hub with collected answers
 */

export class DiagnosticChatManager {
    constructor() {
        this.isActive = false;
        this.sessionId = null;
        this.diagnosticTest = null;
        this.progress = {
            current: 0,
            total: 0,
            percentage: 0,
            isComplete: false
        };
        this.onProgressUpdate = null;
        this.onComplete = null;
        this.onQuestionReceived = null;
        
        console.log('[DiagnosticChatManager] Initialized');
    }

    /**
     * Check if there's a pending diagnostic test in sessionStorage
     * @returns {boolean} True if a diagnostic test is pending
     */
    checkForPendingDiagnostic() {
        // Check for standalone tutor hub format
        const pendingTest = sessionStorage.getItem('diagnosticChatPending');
        if (pendingTest) {
            try {
                const data = JSON.parse(pendingTest);
                if (data && data.diagnosticTest) {
                    console.log('[DiagnosticChatManager] Found pending diagnostic test (standalone):', data.diagnosticTest.topic);
                    return true;
                }
            } catch (e) {
                console.error('[DiagnosticChatManager] Error parsing pending diagnostic:', e);
            }
        }
        
        // Check for embedded tutor hub format
        const embeddedTest = sessionStorage.getItem('pendingDiagnosticTest');
        if (embeddedTest) {
            try {
                const data = JSON.parse(embeddedTest);
                if (data && data.questions) {
                    // Convert to the expected format
                    sessionStorage.setItem('diagnosticChatPending', JSON.stringify({
                        diagnosticTest: data
                    }));
                    sessionStorage.removeItem('pendingDiagnosticTest');
                    console.log('[DiagnosticChatManager] Found pending diagnostic test (embedded):', data.topic);
                    return true;
                }
            } catch (e) {
                console.error('[DiagnosticChatManager] Error parsing embedded diagnostic:', e);
            }
        }
        
        return false;
    }

    /**
     * Start the diagnostic chat session
     * @returns {Promise<{success: boolean, firstQuestion?: string, error?: string}>}
     */
    async startDiagnosticSession() {
        const pendingTest = sessionStorage.getItem('diagnosticChatPending');
        if (!pendingTest) {
            return { success: false, error: 'No pending diagnostic test found' };
        }

        try {
            const data = JSON.parse(pendingTest);
            this.diagnosticTest = data.diagnosticTest;

            const response = await fetch('/api/diagnostic-chat/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ diagnostic_test: this.diagnosticTest })
            });

            const result = await response.json();

            if (result.success) {
                this.isActive = true;
                this.sessionId = result.session_id;
                this.progress = {
                    current: result.progress.current,
                    total: result.progress.total,
                    percentage: result.progress.percentage,
                    isComplete: result.progress.is_complete
                };

                sessionStorage.removeItem('diagnosticChatPending');
                
                console.log('[DiagnosticChatManager] Session started:', this.sessionId);
                console.log('[DiagnosticChatManager] First question:', result.first_question);

                if (this.onProgressUpdate) {
                    this.onProgressUpdate(this.progress);
                }

                return { 
                    success: true, 
                    firstQuestion: result.first_question,
                    topic: result.topic
                };
            } else {
                return { success: false, error: result.error || 'Failed to start session' };
            }
        } catch (error) {
            console.error('[DiagnosticChatManager] Error starting session:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Submit a user's answer and get the next question
     * @param {string} userResponse The user's natural language answer
     * @param {number} confidence Confidence level from slider (0-1), optional
     * @returns {Promise<{success: boolean, nextQuestion?: string, isComplete?: boolean, error?: string}>}
     */
    async submitAnswer(userResponse, confidence = null) {
        if (!this.isActive || !this.sessionId) {
            return { success: false, error: 'No active diagnostic session' };
        }

        try {
            const payload = {
                session_id: this.sessionId,
                user_response: userResponse
            };
            
            // Include confidence if provided from slider
            if (confidence !== null) {
                payload.slider_confidence = confidence;
            }
            
            const response = await fetch('/api/diagnostic-chat/answer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.success) {
                this.progress = {
                    current: result.progress.current,
                    total: result.progress.total,
                    percentage: result.progress.percentage,
                    isComplete: result.progress.is_complete
                };

                if (this.onProgressUpdate) {
                    this.onProgressUpdate(this.progress);
                }
                
                // Sync the answered question to embedded Tutor Hub (if available)
                if (result.answered_question && window.TutorApp && typeof window.TutorApp.ingestChatAnswer === 'function') {
                    window.TutorApp.ingestChatAnswer({
                        questionId: result.answered_question.question_id,
                        answer: result.answered_question.answer,
                        confidence: result.answered_question.confidence,
                        reasoning: result.answered_question.reasoning
                    });
                    console.log('[DiagnosticChatManager] Synced answer to Tutor Hub:', result.answered_question.question_id);
                }

                if (result.needs_clarification) {
                    console.log('[DiagnosticChatManager] Answer needs clarification');
                    return {
                        success: true,
                        needsClarification: true,
                        clarificationPrompt: result.clarification_prompt || "I didn't quite catch your answer. Could you please rephrase that?"
                    };
                }

                if (result.is_complete) {
                    console.log('[DiagnosticChatManager] All questions answered!');
                    return {
                        success: true,
                        isComplete: true,
                        completionMessage: result.completion_message
                    };
                } else {
                    return {
                        success: true,
                        isComplete: false,
                        nextQuestion: result.next_question
                    };
                }
            } else {
                return { success: false, error: result.error || 'Failed to submit answer' };
            }
        } catch (error) {
            console.error('[DiagnosticChatManager] Error submitting answer:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Complete the diagnostic and prepare for evaluation
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async completeDiagnostic() {
        if (!this.isActive || !this.sessionId) {
            return { success: false, error: 'No active diagnostic session' };
        }

        try {
            const response = await fetch('/api/diagnostic-chat/submit-for-evaluation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: this.sessionId })
            });

            const result = await response.json();

            if (result.success && result.ready_for_evaluation) {
                sessionStorage.setItem('evaluationPayload', JSON.stringify(result.evaluation_payload));
                
                this.isActive = false;
                this.sessionId = null;

                console.log('[DiagnosticChatManager] Diagnostic complete, evaluation payload stored');

                if (this.onComplete) {
                    this.onComplete();
                }

                return { success: true };
            } else {
                return { success: false, error: result.error || 'Failed to prepare evaluation' };
            }
        } catch (error) {
            console.error('[DiagnosticChatManager] Error completing diagnostic:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Cancel the current diagnostic session
     */
    cancelDiagnostic() {
        this.isActive = false;
        this.sessionId = null;
        this.diagnosticTest = null;
        this.progress = { current: 0, total: 0, percentage: 0, isComplete: false };
        
        sessionStorage.removeItem('diagnosticChatPending');
        
        console.log('[DiagnosticChatManager] Diagnostic cancelled');
        
        if (this.onProgressUpdate) {
            this.onProgressUpdate(null);
        }
    }

    /**
     * Redirect back to Tutor Hub for evaluation
     */
    redirectToEvaluation() {
        const returnTo = sessionStorage.getItem('returnToTutorHub');
        
        // Check if this is a subtopic quiz - call submitQuiz() instead of full evaluation
        if (this.diagnosticTest?.isSubtopicQuiz) {
            console.log('[DiagnosticChatManager] Subtopic quiz detected - calling submitQuiz()');
            sessionStorage.removeItem('returnToTutorHub');
            
            // Open the embedded tutor hub if needed
            if (typeof window.openTutorMode === 'function') {
                window.openTutorMode();
            }
            
            // Submit the quiz for grading
            setTimeout(() => {
                if (window.TutorApp && typeof window.TutorApp.submitQuiz === 'function') {
                    window.TutorApp.submitQuiz();
                } else {
                    console.error('[DiagnosticChatManager] TutorApp.submitQuiz not available');
                }
            }, 150);
            
            return;
        }
        
        if (returnTo === 'embedded') {
            sessionStorage.removeItem('returnToTutorHub');
            
            // Open the embedded tutor hub
            if (typeof window.openTutorMode === 'function') {
                window.openTutorMode();
            }
            
            // Use the evaluation payload from chat session
            setTimeout(() => {
                if (window.TutorApp) {
                    // Get the evaluation payload stored by completeDiagnostic()
                    const evalPayloadStr = sessionStorage.getItem('evaluationPayload');
                    if (evalPayloadStr) {
                        try {
                            const evalPayload = JSON.parse(evalPayloadStr);
                            // Restore the diagnostic test and state to TutorState
                            if (evalPayload.assessment_spec && window.TutorState) {
                                window.TutorState.diagnosticTest = evalPayload.assessment_spec;
                                
                                // Reconstruct answers from learner_session
                                // The chat-based answers use: { question_id, free_text_answer, reasoning, confidence }
                                if (evalPayload.learner_session && evalPayload.learner_session.answers) {
                                    window.TutorState.answers = {};
                                    const answers = evalPayload.learner_session.answers;
                                    
                                    answers.forEach(ans => {
                                        // Find the question to determine if it's MCQ or free text
                                        const question = evalPayload.assessment_spec.questions?.find(q => q.id === ans.question_id);
                                        const isMCQ = question?.question_type === 'mcq';
                                        
                                        window.TutorState.answers[ans.question_id] = {
                                            // For MCQ, try to extract the option ID from the free text answer
                                            selected_answer: isMCQ ? (ans.free_text_answer?.match(/^[A-D]$/i)?.[0]?.toUpperCase() || ans.free_text_answer?.charAt(0)?.toUpperCase() || null) : null,
                                            reasoning_text: ans.free_text_answer || ans.reasoning || '',
                                            confidence: ans.confidence || 0.5
                                        };
                                    });
                                    
                                    // Set question index to last question (completed)
                                    window.TutorState.currentQuestionIndex = answers.length - 1;
                                }
                                
                                // Mark step 1 (diagnostic) as completed
                                window.TutorState.completedSteps = window.TutorState.completedSteps || {};
                                window.TutorState.completedSteps[1] = true;
                                window.TutorState.isInlineMode = false;
                                
                                console.log('[DiagnosticChatManager] Restored TutorState from chat answers:', {
                                    diagnosticTest: window.TutorState.diagnosticTest?.topic,
                                    answersCount: Object.keys(window.TutorState.answers).length,
                                    completedSteps: window.TutorState.completedSteps
                                });
                            }
                            window.TutorApp.showView('results');
                            window.TutorApp.runFullEvaluation();
                        } catch (e) {
                            console.error('[DiagnosticChatManager] Error parsing evaluation payload:', e);
                            window.TutorApp.showView('results');
                        }
                    } else {
                        window.TutorApp.showView('results');
                    }
                }
            }, 150);
        } else {
            window.location.href = '/tutor?view=results';
        }
    }

    /**
     * Get current progress info for UI display
     * @returns {{current: number, total: number, percentage: number, isComplete: boolean}|null}
     */
    getProgress() {
        return this.isActive ? this.progress : null;
    }

    /**
     * Check if diagnostic mode is currently active
     * @returns {boolean}
     */
    isDiagnosticActive() {
        return this.isActive;
    }

    /**
     * Get the topic being tested
     * @returns {string|null}
     */
    getTopic() {
        return this.diagnosticTest?.topic || null;
    }

    /**
     * Get the current question ID being answered
     * @returns {string|null}
     */
    getCurrentQuestionId() {
        if (!this.isActive || !this.diagnosticTest?.questions) {
            return null;
        }
        const currentIndex = this.progress.current;
        const question = this.diagnosticTest.questions[currentIndex];
        return question?.id || null;
    }
}

window.DiagnosticChatManager = DiagnosticChatManager;
